import { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Button } from '@ag1/ui';

import {
  SanityKeyed,
  type VideoReviewsTabbed as VideoReviewsTabbedProps
} from 'types/studio/studio';
import { navigationClickEvent } from 'analytics/events/studio';
import { toPlainText } from '@portabletext/toolkit';
import ModalYoutubeVideos from '@portal/shared/ui/ModalYoutubeVideos/ModalYoutubeVideos';

import { VideoReviewEntry } from './components/VideoReviewEntry';
import { CommonSectionProps } from '../../types';
import { Pagination } from '../Pagination/Pagination';
import styles from './VideoReviews.module.scss';

interface Props extends SanityKeyed<VideoReviewsTabbedProps>, CommonSectionProps {
  scrollToTop: () => void;
}

export const VideoReviews = ({
  _type: sectionType,
  filterTabs,
  allTabText = 'All Reviews',
  videoReviews,
  limitReviewsPerPage = 12,
  scrollToTop
}: Props) => {
  const [activeFilterTab, setActiveFilterTab] = useState(-1);

  // Active page for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);

  const filteredReviewVideos = useMemo(() => {
    if (activeFilterTab === -1) return videoReviews;
    return videoReviews.filter(
      ({ filterTags }) => filterTags?.includes(filterTabs[activeFilterTab])
    );
  }, [activeFilterTab, filterTabs, videoReviews]);

  const handleItemClick = useCallback(
    (index: number) => {
      setCurrentVideoIndex(index);
      setVideoModalOpen(true);

      const reviewCardTitle = filteredReviewVideos[index].title;
      const reviewCardSubheader = filteredReviewVideos[index].reviewerName;

      navigationClickEvent({
        location: `${sectionType} video ${index + 1}`,
        navigationType: 'video',
        clickText: 'play',
        header: reviewCardTitle ? toPlainText(reviewCardTitle) : '',
        subheader: reviewCardSubheader
      });
    },
    [setCurrentVideoIndex, setVideoModalOpen, filteredReviewVideos, sectionType]
  );

  const changeFilterTab = useCallback(
    (index: number) => {
      setCurrentPage(1);
      scrollToTop();
      setActiveFilterTab(index);
    },
    [setCurrentPage, scrollToTop, setActiveFilterTab]
  );

  const currentVideoUrlPseudoArray = useMemo(
    () => [...filteredReviewVideos.map((review) => review.video)],
    [filteredReviewVideos]
  );

  const visibleVideoReviews = filteredReviewVideos.slice(
    (currentPage - 1) * limitReviewsPerPage,
    currentPage * limitReviewsPerPage
  );

  return (
    <>
      <div className={styles.filterTabs}>
        <Button
          variant="outline"
          theme="new"
          size="small"
          onClick={() => changeFilterTab(-1)}
          className={clsx(
            styles.filterTabButton,
            activeFilterTab === -1 ? styles.activeFilterTabButton : ''
          )}>
          {allTabText}
        </Button>
        {filterTabs.map((tabName, index) => (
          <Button
            key={tabName}
            variant="outline"
            theme="new"
            size="small"
            onClick={() => changeFilterTab(index)}
            className={clsx(
              styles.filterTabButton,
              activeFilterTab === index ? styles.activeFilterTabButton : ''
            )}>
            {tabName}
          </Button>
        ))}
      </div>
      <div className={clsx(styles.videoReviewsGrid)}>
        {visibleVideoReviews.map((videoReviewItem, index) => (
          <VideoReviewEntry
            onClick={() => handleItemClick((currentPage - 1) * limitReviewsPerPage + index)}
            item={videoReviewItem}
            key={videoReviewItem._key}
            imageSizes={{ xxs: 6, s: 4, ssm: 3, l: '200px' }}
          />
        ))}
      </div>
      <Pagination
        currentPage={currentPage}
        totalItemsCount={filteredReviewVideos.length}
        itemsPerPage={limitReviewsPerPage}
        setCurrentPage={setCurrentPage}
        onPageChange={scrollToTop}
      />
      <ModalYoutubeVideos
        videoUrls={currentVideoUrlPseudoArray}
        isOpen={isVideoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        videoIndex={currentVideoIndex}
        theme="new"
      />
    </>
  );
};
